<template>
  <div class="relative inline-block w-max" ref="triggerRef" :class="customClass">
    <!-- Trigger Button -->
    <button
      @click.stop="toggleDropdown"
      class="dropdown-trigger"
      :class="triggerStyle ?? 'px-4 py-2 bg-blue-500 text-white rounded w-full'"
    >
      <slot name="trigger">Actions</slot>
    </button>

    <!-- Dropdown Content -->
    <Teleport to="body">
      <Transition name="fade">
        <div
          v-if="isOpen"
          ref="dropdownRef"
          :data-dropdown-id="id"
          class="dropdown-menu bg-white shadow-md rounded-lg w-max"
          :style="{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            minWidth: dropdownPosition.minWidth,
          }"
          @click.stop="handleContentClick"
        >
          <slot name="contents"></slot>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { activeDropdownId, openDropdownHierarchy, dropdownDOMRegistry as domRegistry } from '@/composables/globalDropdown';
import { useEventListener } from '@vueuse/core';
import { inject, nextTick, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue';

const props = defineProps<{
  triggerStyle?: string;
  closeOnOutsideClick?: boolean;
  closeOnSelect?: boolean;
  customClass?: string;
}>();

const emits = defineEmits(['open']);
const NAV_HEIGHT = 70; // adjust as per your navbar height
const id = Math.random().toString(36).substring(2, 9);
const parentId = inject<string | null>('dropdownParentId', null);
provide('dropdownParentId', id);

const isOpen = ref(false);
const triggerRef = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);

const dropdownPosition = ref({ top: '0px', left: '0px', minWidth: 'auto' });

const isInsideDropdown = (dropId: string, target: HTMLElement): boolean => {
  const refs = domRegistry.get(dropId);
  if (!refs) return false;
  return !!(refs.trigger?.contains(target) || refs.content?.contains(target));
};

const myIndexInHierarchy = () => openDropdownHierarchy.value.indexOf(id);

// const toggleDropdown = () => {
//   if (!isOpen.value) {
//     const newHierarchy = parentId
//       ? [...openDropdownHierarchy.value.filter((i) => i !== id), id]
//       : [id];
//     openDropdownHierarchy.value = newHierarchy;
//     activeDropdownId.value = id;
//   } else {
//     openDropdownHierarchy.value = [];
//     activeDropdownId.value = null;
//   }
// };
const toggleDropdown = () => {
  if (!isOpen.value) {
    const newHierarchy = parentId
      ? [...openDropdownHierarchy.value.filter((i) => i !== id), id]
      : [id];
    openDropdownHierarchy.value = newHierarchy;
    activeDropdownId.value = id;
  } else {
    // Do NOT wipe the whole array — parent(s) before us must stay open
    const myIdx = openDropdownHierarchy.value.indexOf(id);
    if (myIdx !== -1) {
      openDropdownHierarchy.value = openDropdownHierarchy.value.slice(0, myIdx);
      activeDropdownId.value =
        openDropdownHierarchy.value[openDropdownHierarchy.value.length - 1] ?? null;
    } else {
      openDropdownHierarchy.value = [];
      activeDropdownId.value = null;
    }
  }
};

const positionDropdown = async () => {
  await nextTick();
  requestAnimationFrame(() => {
    if (!triggerRef.value || !dropdownRef.value) return;
    const tr = triggerRef.value.getBoundingClientRect();
    const dr = dropdownRef.value.getBoundingClientRect();
    const top =
      window.innerHeight - tr.bottom >= dr.height
        ? tr.bottom + window.scrollY
        : tr.top - dr.height + window.scrollY;
    const left =
      tr.left >= dr.width ? tr.left + tr.width - dr.width : tr.left;
    dropdownPosition.value = { top: `${top}px`, left: `${left}px`, minWidth: `${tr.width}px` };
  });
};

// ---------------------------------------------------------------------------
// Click inside THIS dropdown's content panel
// ---------------------------------------------------------------------------
const handleContentClick = (event: MouseEvent) => {
  const myIdx = myIndexInHierarchy();
  if (myIdx === -1) return;

  const target = event.target as HTMLElement;
  const hasOpenChildren = myIdx < openDropdownHierarchy.value.length - 1;

  if (hasOpenChildren) {
    const clickedInsideChild = openDropdownHierarchy.value
      .slice(myIdx + 1)
      .some((childId) => isInsideDropdown(childId, target));

    if (!clickedInsideChild) {
      openDropdownHierarchy.value = openDropdownHierarchy.value.slice(0, myIdx + 1);
      activeDropdownId.value = id;
      return;
    }
  }

  if (props.closeOnSelect) {
    openDropdownHierarchy.value = openDropdownHierarchy.value.slice(0, myIdx);
    activeDropdownId.value =
      openDropdownHierarchy.value[openDropdownHierarchy.value.length - 1] ?? null;
  }
};

// ---------------------------------------------------------------------------
// Click outside (document level)
// ---------------------------------------------------------------------------
// const handleOutsideClick = (event: MouseEvent) => {
//   if (props.closeOnOutsideClick === false) return;
//   if (!isOpen.value) return;

//   if (openDropdownHierarchy.value[0] !== id) return;

//   const target = event.target as HTMLElement;

//   const clickedInsideAny = openDropdownHierarchy.value.some((dropId) =>
//     isInsideDropdown(dropId, target)
//   );

//   if (!clickedInsideAny) {
//     openDropdownHierarchy.value = [];
//     activeDropdownId.value = null;
//   }
// };
const handleOutsideClick = (event: MouseEvent) => {
  if (props.closeOnOutsideClick === false) return;
  if (!isOpen.value) return;

  const target = event.target as HTMLElement;

  // Check if click landed inside ANY currently open dropdown (trigger or content)
  const clickedInsideAny = openDropdownHierarchy.value.some((dropId) =>
    isInsideDropdown(dropId, target)
  );

  if (clickedInsideAny) return;

  const myIdx = openDropdownHierarchy.value.indexOf(id);

  if (myIdx === 0) {
    // I am the root — close everything
    openDropdownHierarchy.value = [];
    activeDropdownId.value = null;
  } else if (myIdx > 0) {
    // I am a child — only close myself and anything after me, keep parents open
    openDropdownHierarchy.value = openDropdownHierarchy.value.slice(0, myIdx);
    activeDropdownId.value =
      openDropdownHierarchy.value[openDropdownHierarchy.value.length - 1] ?? null;
  }
};

const isElementInViewport = (el: HTMLElement) => {
  const rect = el.getBoundingClientRect();
  return rect.top >= NAV_HEIGHT && rect.left >= 0 &&
    rect.bottom <= window.innerHeight && rect.right <= window.innerWidth;
};

const handleScroll = () => {
  if (!triggerRef.value) return;
  if (!isElementInViewport(triggerRef.value)) {
    if (isOpen.value) { openDropdownHierarchy.value = []; activeDropdownId.value = null; }
  } else if (isOpen.value) {
    positionDropdown();
  }
};

watch(openDropdownHierarchy, (chain) => {
  const willBeOpen = chain.includes(id);
  isOpen.value = willBeOpen;
  emits('open', willBeOpen);
});

watch(isOpen, async (visible) => {
  if (visible) {
    await nextTick();
    domRegistry.set(id, { trigger: triggerRef.value, content: dropdownRef.value });
    await positionDropdown();
  } else {
    domRegistry.delete(id);
  }
});

onMounted(() => {
  useEventListener(document, 'click', handleOutsideClick);

  window.addEventListener('scroll', handleScroll, true);
  useEventListener(window, 'resize', positionDropdown);
});

onBeforeUnmount(() => {
  domRegistry.delete(id);
  document.removeEventListener('click', handleOutsideClick);
  window.removeEventListener('scroll', handleScroll);
});
</script>

<style scoped>
.dropdown-trigger {
  cursor: pointer;
  transition: background-color 0.2s;
}
.dropdown-menu {
  z-index: 10030;
  border: 1px solid #e5e7eb;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: scale(0.98);
}
</style>
